import type { ComponentProps, ElementType } from 'react'
import {
  AlertCircle,
  ArrowUpRight,
  Bath,
  BedDouble,
  Bell,
  CalendarDays,
  Car,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  CreditCard,
  Download,
  Euro,
  Filter,
  Gift,
  Heart,
  Home,
  Info,
  List,
  Lock,
  LogOut,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  Minus,
  Pencil,
  Phone,
  Plus,
  Printer,
  Search,
  Settings,
  Share2,
  Shield,
  ShoppingCart,
  SlidersHorizontal,
  Star,
  Ticket,
  Trash2,
  Upload,
  User,
  Users,
  Wifi,
  X,
  AlertTriangle,
} from 'lucide-react'
import { getActiveThemeSlug, isStructuralThemeSlug, type ThemeSlug } from '@/themes/registry'

export type ThemedIconComponent = ElementType

/** Tier 1 IconKey inventory — DESIGN_AIRBNB.md §7.10 (Navigation → Contact). */
export const TIER_ONE_ICON_KEYS = [
  'home',
  'reservation',
  'calendar',
  'heart',
  'user',
  'users',
  'bell',
  'cart',
  'search',
  'filter',
  'sliders',
  'map-pin',
  'star',
  'check-circle',
  'info',
  'mail',
  'phone',
  'logout',
  'settings',
  'credit-card',
  'ticket',
  'gift',
  'menu',
  'list',
  'close',
  'chevron-left',
  'chevron-right',
  'chevron-down',
  'chevron-up',
  'arrow-external',
  'shield',
  'bed',
  'bath',
  'clock',
  'euro',
  'car',
  'wifi',
  'share',
  'download',
  'upload',
  'print',
  'edit',
  'trash',
  'plus',
  'minus',
  'check',
  'warning',
  'error-circle',
  'success-circle',
  'lock',
  'chat',
] as const

export type IconKey = (typeof TIER_ONE_ICON_KEYS)[number]

const LUCIDE_ICON_REGISTRY = {
  home: Home,
  reservation: Home,
  calendar: CalendarDays,
  heart: Heart,
  user: User,
  users: Users,
  bell: Bell,
  cart: ShoppingCart,
  search: Search,
  filter: Filter,
  sliders: SlidersHorizontal,
  'map-pin': MapPin,
  star: Star,
  'check-circle': CheckCircle2,
  info: Info,
  mail: Mail,
  phone: Phone,
  logout: LogOut,
  settings: Settings,
  'credit-card': CreditCard,
  ticket: Ticket,
  gift: Gift,
  menu: Menu,
  list: List,
  close: X,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'chevron-down': ChevronDown,
  'chevron-up': ChevronUp,
  'arrow-external': ArrowUpRight,
  shield: Shield,
  bed: BedDouble,
  bath: Bath,
  clock: Clock,
  euro: Euro,
  car: Car,
  wifi: Wifi,
  share: Share2,
  download: Download,
  upload: Upload,
  print: Printer,
  edit: Pencil,
  trash: Trash2,
  plus: Plus,
  minus: Minus,
  check: Check,
  warning: AlertTriangle,
  'error-circle': AlertCircle,
  'success-circle': CheckCircle2,
  lock: Lock,
  chat: MessageCircle,
} satisfies Record<IconKey, ThemedIconComponent>

type ThemeIconsModule = {
  ICON_REGISTRY?: Partial<Record<IconKey, ThemedIconComponent>>
}

const structuralIconModules: Record<string, ThemeIconsModule> =
  import.meta.env.VITE_ACTIVE_THEME && import.meta.env.VITE_ACTIVE_THEME !== 'default'
    ? (import.meta.glob('../../themes/*/icons.ts', { eager: true }) as Record<string, ThemeIconsModule>)
    : {}

function getThemeIconRegistry(themeSlug: ThemeSlug): Partial<Record<IconKey, ThemedIconComponent>> | undefined {
  if (!isStructuralThemeSlug(themeSlug)) {
    return undefined
  }

  // Only the active theme slug — ignore sibling folders (futures, globe, …).
  for (const [key, mod] of Object.entries(structuralIconModules)) {
    if (key.replace(/\\/g, '/').endsWith(`/themes/${themeSlug}/icons.ts`)) {
      return mod.ICON_REGISTRY
    }
  }

  return undefined
}

/** Resolves an IconKey to Lucide (default) or the active structural theme icon map. */
export function resolveThemedIcon(name: IconKey, themeSlug: ThemeSlug = getActiveThemeSlug()): ThemedIconComponent {
  return getThemeIconRegistry(themeSlug)?.[name] ?? LUCIDE_ICON_REGISTRY[name]
}

/** Hook alias for {@link resolveThemedIcon}. */
export function useThemedIcon(name: IconKey): ThemedIconComponent {
  return resolveThemedIcon(name)
}

// Safe at module top level: defers the registry read past the circular-init window.
export function createLazyThemedIcon(name: IconKey): ThemedIconComponent {
  return function LazyThemedIcon(props: ComponentProps<ThemedIconComponent>) {
    const Icon = resolveThemedIcon(name)
    return <Icon {...props} />
  }
}
